import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { adminApi, analyticsApi } from '@/api';
import { GreetingBanner, todayLine } from '@/components/shell';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  MetricCard,
} from '@/components/ui';
import { auditActionTone } from '@/features/admin/status';
import { APPLICATION_LABELS } from '@/features/applications/status';
import { count } from '@/features/analytics/format';
import { GROWTH_SERIES } from '@/features/analytics/series';
import { TrendChart } from '@/features/analytics/TrendChart';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { relativeTime } from '@/utils/format';

const GROWTH_WINDOW_DAYS = 30;
const RECENT_ACTIVITY_ROWS = 6;

/** The pipeline in the order it happens, so the bars read as a funnel. */
const STATUS_ORDER = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'SHORTLISTED',
  'INTERVIEW',
  'DECISION_PENDING',
  'OFFERED',
  'OFFER_ACCEPTED',
  'HIRED',
  'REJECTED',
  'WITHDRAWN',
];

/** Terminal outcomes are shaded apart from the live pipeline. */
const CLOSED_STATUSES = new Set(['REJECTED', 'WITHDRAWN']);

export function AdminOverviewPage() {
  const { user } = useAuth();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: adminApi.getOverview,
  });

  // Secondary panels: the page is useful without them, so they neither block
  // the first paint nor turn the screen into an error if one is unavailable.
  const { data: growth } = useQuery({
    queryKey: ['platform-analytics', 'growth', GROWTH_WINDOW_DAYS],
    queryFn: () => analyticsApi.getPlatformGrowth(GROWTH_WINDOW_DAYS),
  });

  const { data: audit } = useQuery({
    queryKey: ['admin-audit', 'recent'],
    queryFn: () => adminApi.listAuditLog({ page: 1, page_size: RECENT_ACTIVITY_ROWS }),
  });

  if (isPending) return <LoadingState label="Loading platform overview…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const statuses = data.applications_by_status ?? {};
  const statusRows = STATUS_ORDER.filter((status) => (statuses[status] ?? 0) > 0).map((status) => ({
    status,
    value: statuses[status],
  }));
  const statusPeak = Math.max(1, ...statusRows.map((row) => row.value));

  const queues = [
    {
      key: 'verifications',
      label: 'Company verifications',
      value: data.pending_verifications,
      to: paths.admin.verifications,
      icon: 'verification',
      idle: 'Nothing waiting for approval.',
      busy: 'Companies cannot publish until a decision is made.',
    },
    {
      key: 'internships',
      label: 'Internship approvals',
      value: data.pending_internships,
      to: paths.admin.internships,
      icon: 'internship',
      idle: 'Nothing waiting. Only unverified companies queue for approval.',
      busy: 'Listings from unverified companies are held until approved.',
    },
    {
      key: 'documents',
      label: 'Certificates and CVs',
      value: data.pending_documents ?? 0,
      to: paths.admin.documentVerifications,
      icon: 'fileText',
      idle: 'Nothing waiting. Students upload a certificate or ask for a CV check.',
      busy: 'Students are waiting to show a verified badge.',
    },
  ];

  const auditRows = audit?.data ?? [];

  return (
    <div>
      <GreetingBanner
        name={user?.full_name?.split(' ')[0] ?? 'Admin'}
        subtitle={todayLine()}
        action={
          <LinkButton to={paths.admin.analytics} variant="light">
            Full analytics
          </LinkButton>
        }
        className="mb-6"
      />

      {/* -- headline numbers ------------------------------------------------ */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Students"
          value={count(data.students)}
          hint="Registered student profiles"
          icon="student"
          tone="brand"
        />
        <MetricCard
          label="Companies"
          value={count(data.companies)}
          hint="Registered workspaces"
          icon="company"
          tone="brand"
        />
        <MetricCard
          label="Live internships"
          value={count(data.internships_published)}
          hint="Published listings, including any past their deadline"
          icon="internship"
          tone="success"
        />
        <MetricCard
          label="Applications"
          value={count(data.applications)}
          hint="Submitted all time"
          icon="application"
          tone="neutral"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* -- work waiting on an admin -------------------------------------- */}
        <section className="lg:col-span-1">
          <Card className="h-full">
            <CardHeader title="Needs your decision" description="Queues only an admin can clear." />
            <ul className="space-y-3">
              {queues.map((queue) => {
                const waiting = queue.value > 0;
                return (
                  <li key={queue.key}>
                    <Link
                      to={queue.to}
                      className={`rounded-card flex items-center gap-3 border p-3 transition duration-200 hover:shadow-md ${
                        waiting
                          ? 'border-warning-200 bg-warning-50/60 hover:border-warning-300'
                          : 'border-line bg-surface hover:border-brand-200'
                      }`}
                    >
                      <span
                        className={`grid size-9 shrink-0 place-items-center rounded-lg ${
                          waiting
                            ? 'bg-warning-100 text-warning-800'
                            : 'bg-surface-muted text-ink-muted'
                        }`}
                      >
                        <Icon name={queue.icon} size={18} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="text-ink block text-sm font-medium">{queue.label}</span>
                        <span className="text-ink-muted block text-xs">
                          {waiting ? queue.busy : queue.idle}
                        </span>
                      </span>
                      <span
                        className={`shrink-0 text-2xl font-semibold tabular-nums ${
                          waiting ? 'text-warning-700' : 'text-ink-muted'
                        }`}
                      >
                        {queue.value}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            <dl className="border-line mt-4 grid grid-cols-2 gap-4 border-t pt-4">
              <div>
                <dt className="text-ink-muted text-xs">Suspended or banned</dt>
                <dd className="text-ink mt-0.5 text-lg font-semibold tabular-nums">
                  {count(data.suspended_users)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-muted text-xs">New accounts in 7 days</dt>
                <dd className="text-ink mt-0.5 text-lg font-semibold tabular-nums">
                  {count(data.signups_last_7_days)}
                </dd>
              </div>
            </dl>
          </Card>
        </section>

        {/* -- growth --------------------------------------------------------- */}
        <section className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader
              title="Growth"
              description={`Signups, listings and applications over the last ${GROWTH_WINDOW_DAYS} days.`}
              action={
                <LinkButton to={paths.admin.analytics} variant="secondary" size="sm">
                  Change period
                </LinkButton>
              }
            />
            <TrendChart
              points={growth?.data ?? []}
              series={GROWTH_SERIES}
              granularity={growth?.meta?.granularity}
              emptyLabel="Nothing was created in this period"
            />
          </Card>
        </section>

        {/* -- pipeline ------------------------------------------------------- */}
        <section className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader
              title="Applications by status"
              description="Where every application on the platform currently sits."
            />
            {statusRows.length === 0 ? (
              <EmptyState
                compact
                title="No applications yet"
                description="This fills in as students start applying."
              />
            ) : (
              <ul className="space-y-2.5">
                {statusRows.map((row) => (
                  <li key={row.status} className="flex items-center gap-3">
                    <span className="text-ink-muted w-24 shrink-0 truncate text-sm sm:w-28">
                      {APPLICATION_LABELS[row.status] ?? row.status}
                    </span>
                    <span className="bg-surface-muted h-5 min-w-0 flex-1 overflow-hidden rounded">
                      <span
                        className={`block h-full rounded ${
                          CLOSED_STATUSES.has(row.status) ? 'bg-ink-muted/40' : 'bg-chart-1'
                        }`}
                        style={{ width: `${Math.max(2, (row.value / statusPeak) * 100)}%` }}
                      />
                    </span>
                    <span className="text-ink w-12 shrink-0 text-right text-sm font-medium tabular-nums">
                      {count(row.value)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>

        {/* -- audit ---------------------------------------------------------- */}
        <section className="lg:col-span-1">
          <Card className="h-full">
            <CardHeader
              title="Recent activity"
              action={
                <LinkButton to={paths.admin.audit} variant="secondary" size="sm">
                  View all
                </LinkButton>
              }
            />
            {auditRows.length === 0 ? (
              <EmptyState
                compact
                title="Nothing logged yet"
                description="Actions appear here as they happen."
              />
            ) : (
              <ol className="space-y-3">
                {auditRows.map((row) => (
                  <li key={row.id} className="flex items-start gap-2">
                    <span className="min-w-0 flex-1">
                      <Badge tone={auditActionTone(row.action)}>{row.action}</Badge>
                      <span className="text-ink-muted mt-1 block text-xs">
                        {row.actor_role ?? 'system'}
                        {row.resource_type ? ` · ${row.resource_type}` : ''}
                      </span>
                    </span>
                    <span className="text-ink-muted shrink-0 text-xs whitespace-nowrap">
                      {relativeTime(row.created_at)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </section>
      </div>
    </div>
  );
}
